import { env } from "../config/env";
import { normalizeFormsMeGrants } from "../lib/forms/formGrants";
import { normalizeUserOrganisations } from "../lib/organisations/normalize";
import type { UserOrganisation } from "../types/organisation";
import {
  ApiError,
  apiRequest,
  getAccessToken,
  setFormUserId,
} from "./client";

export type AuthUser = {
  id: string | number;
  chmId?: string | null;
  name: string;
  email: string;
  phone?: string | null;
};

export type GetUserResponse = {
  user: AuthUser;
  roles: string[];
  organisations: UserOrganisation[];
  grantedFormIds: number[];
  manageGrantedFormIds: number[];
  authenticatedVia?: string | null;
  formsAccessError?: string | null;
};

type ChmIdentityProfile = {
  id?: unknown;
  firstName?: unknown;
  lastName?: unknown;
  middleName?: unknown;
  email?: unknown;
  phone?: unknown;
};

type FormsAccessResponse = {
  id?: unknown;
  chmId?: unknown;
  roles?: unknown;
  organisations?: unknown;
  grantedFormIds?: unknown;
  granted_form_ids?: unknown;
  manageGrantedFormIds?: unknown;
  manage_granted_form_ids?: unknown;
  grants?: unknown;
  authenticatedVia?: unknown;
};

type LegacyFormsUserResponse = {
  user?: {
    id?: unknown;
    chm_id?: unknown;
    name?: unknown;
    email?: unknown;
    phone?: unknown;
  };
  roles?: unknown;
  organisations?: unknown;
};

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readRoles(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((role): role is string => typeof role === "string")
    : [];
}

function identityUser(body: ChmIdentityProfile): AuthUser {
  const id = readString(body.id).trim();
  if (!id) throw new Error("Некорректный ответ профиля CHM ID");

  const name = [readString(body.firstName), readString(body.lastName)]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");
  const email = readString(body.email).trim();

  return {
    id,
    chmId: id,
    name: name || email || "Пользователь",
    email,
    phone: readString(body.phone).trim() || null,
  };
}

async function fetchIdentityProfile(options?: {
  skipUnauthorizedHandler?: boolean;
}): Promise<AuthUser> {
  const body = await apiRequest<ChmIdentityProfile>("/api/v1/users/me", {
    baseUrl: env.identityApiBaseUrl,
    auth: false,
    skipUnauthorizedHandler: options?.skipUnauthorizedHandler,
  });
  return identityUser(body);
}

async function fetchFormsAccess(options?: {
  skipUnauthorizedHandler?: boolean;
}): Promise<Omit<GetUserResponse, "user" | "formsAccessError">> {
  const body = await apiRequest<FormsAccessResponse>("/api/v1/forms/me", {
    skipUnauthorizedHandler: options?.skipUnauthorizedHandler,
  });
  const grants = normalizeFormsMeGrants(body);

  return {
    roles: readRoles(body.roles),
    organisations: normalizeUserOrganisations(body.organisations),
    grantedFormIds: grants.grantedFormIds,
    manageGrantedFormIds: grants.manageGrantedFormIds,
    authenticatedVia:
      typeof body.authenticatedVia === "string" ? body.authenticatedVia : null,
  };
}

async function fetchLegacyFormsUser(options?: {
  skipUnauthorizedHandler?: boolean;
}): Promise<GetUserResponse> {
  const body = await apiRequest<LegacyFormsUserResponse>("/api/v1/get-user", {
    skipUnauthorizedHandler: options?.skipUnauthorizedHandler,
    bearerToken: getAccessToken(),
  });
  const rawUser = body.user ?? {};
  const id = Number(rawUser.id);
  if (!Number.isFinite(id)) {
    throw new Error("Некорректный ответ профиля форм");
  }

  return {
    user: {
      id: Math.trunc(id),
      chmId: readString(rawUser.chm_id).trim() || null,
      name: readString(rawUser.name),
      email: readString(rawUser.email),
      phone: readString(rawUser.phone).trim() || null,
    },
    roles: readRoles(body.roles),
    organisations: normalizeUserOrganisations(body.organisations),
    grantedFormIds: [],
    manageGrantedFormIds: [],
    authenticatedVia: "passport",
  };
}

function formsAccessError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      return "Вы вошли через CHM ID, но пользователь не добавлен в систему форм.";
    }
    if (error.status === 404) {
      return "Профиль форм недоступен: маршрут /api/v1/forms/me не настроен.";
    }
    return `Не удалось загрузить права в системе форм: ${error.message}`;
  }
  return "Не удалось загрузить права и организации в системе форм.";
}

function canUseLegacyDevAuth(): boolean {
  return import.meta.env.DEV && Boolean(env.apiToken);
}

/** CHM ID подтверждает сессию, а forms/me возвращает права конструктора. */
export async function getUser(options?: {
  skipUnauthorizedHandler?: boolean;
}): Promise<GetUserResponse> {
  setFormUserId(null);

  // CHM ID cookies недоступны localhost; dev-токен явно включает legacy-вход.
  if (canUseLegacyDevAuth()) return fetchLegacyFormsUser(options);

  let user: AuthUser;

  try {
    user = await fetchIdentityProfile(options);
  } catch (error) {
    throw error;
  }

  setFormUserId(user.chmId ?? user.id);

  try {
    const access = await fetchFormsAccess(options);
    return { user, ...access, formsAccessError: null };
  } catch (error) {
    return {
      user,
      roles: [],
      organisations: [],
      grantedFormIds: [],
      manageGrantedFormIds: [],
      authenticatedVia: "chm-id",
      formsAccessError: formsAccessError(error),
    };
  }
}

/** Завершает общую сессию id.chm12.ru. */
export async function logout(): Promise<void> {
  try {
    await apiRequest<void>("/api/v1/auth/logout", {
      baseUrl: env.identityApiBaseUrl,
      method: "POST",
      auth: false,
      skipUnauthorizedHandler: true,
    });
  } finally {
    setFormUserId(null);
  }
}
